import React, {Component} from 'react';
import {connect} from "react-redux";
import {withRouter} from "react-router-dom";
import {navigationAdded} from "../actions/OptionsActions";
import Loader from "@amzn/meridian/loader";
import ErrorPage from "./utils/ErrorPage";
import Table from "@amzn/meridian/table";
import TableRow from "@amzn/meridian/table/table-row";
import TableCell from "@amzn/meridian/table/table-cell";
import Column from "@amzn/meridian/column";
import {
    chainWalk,
    formQueryParams,
    getFilteredLocations,
    getOrDefaultValueForCell
} from "../Utility";
import Link from "@amzn/meridian/link";
import {FormattedMessage} from "react-intl";
import {PackageStatus} from "../enums";
import {getTableCellCount, getTableCellHeader} from "./utils/TableCellUtil";
import RefreshTimer from "./RefreshTimer";
import {FEATURES} from "../constants";

class Cluster extends Component {

    constructor(props) {
        super(props);
        this.state = {
            sortColumn: "cluster",
            sortDirection: "ascending"
        }
    }

    render() {
        const {
            loading, error, stowPackageMetricsMap, inductPackageMetricsMap, stowAssociateMetricsMap, stowBinMetricsMap,
            stowCartMetricsMap
        } = this.props;

        if (error) {
            return <ErrorPage errorId="stow.error" errorMessage="Some error occurred"/>
        }

        const allClusters = [
            ...Object.keys(stowPackageMetricsMap),
            ...Object.keys(inductPackageMetricsMap),
            ...Object.keys(stowAssociateMetricsMap),
            ...Object.keys(stowBinMetricsMap),
            ...Object.keys(stowCartMetricsMap)
        ];

        const distinctClusters = [...new Set(allClusters)];

        if (loading && !distinctClusters.length) {
            return (
                <Column
                    height="400px"
                    alignmentHorizontal="center"
                >
                    <Loader/>
                </Column>
            );
        }
        const filteredClusters = getFilteredLocations(distinctClusters, stowPackageMetricsMap, this.isDRSStowVisibilityEnabled());
        const onSort = (sortDetails) => {
            this.setState({
                sortColumn: sortDetails.sortColumn,
                sortDirection: sortDetails.sortDirection
            })
        }

        const sortColumn = this.state.sortColumn;
        const direction = this.state.sortDirection;
        filteredClusters.sort((c1, c2) => {
            const c1CombinedMetricsMap = this.getCombinedMetricsMap(c1);
            const c2CombinedMetricsMap = this.getCombinedMetricsMap(c2);
            if (c1CombinedMetricsMap[sortColumn] < c2CombinedMetricsMap[sortColumn]) {
                return direction === "ascending" ? -1 : 1
            }
            if (c1CombinedMetricsMap[sortColumn] > c2CombinedMetricsMap[sortColumn]) {
                return direction === "ascending" ? 1 : -1
            }
            return 0
        });

        return (
            <Column>
                <RefreshTimer deps={[stowPackageMetricsMap]} />
                <Table headerRows={2} showDividers={false} showStripes={false} spacing="small" fixHeaderRows={true}
                       fixHeaderRowsFullWidth={true}
                       sortColumn={this.state.sortColumn}
                       sortDirection={this.state.sortDirection}
                       onSort={onSort}
                       data-cy={"clusterTable"}>
                    {this.renderHeadings()}
                    {this.renderSubHeadings()}
                    {filteredClusters.map((cluster) => this.renderClusterRow(cluster))}
                    {filteredClusters.length ? this.renderTotalClusterRow() : null}
                </Table>
            </Column>
        )
    }

    renderHeadings() {
        return (
            <TableRow>
                <TableCell/>
                <TableCell>
                    <FormattedMessage id="cluster.packages" defaultMessage="Packages"/>
                </TableCell>
                <TableCell/>
                <TableCell/>
                <TableCell/>
                <TableCell/>
                <TableCell/>
                {this.isADTAVisibilityEnabled() && <TableCell/>}
                {this.isADTAVisibilityEnabled() && <TableCell/>}
                {this.isCartVisibilityEnabled() && <TableCell/>}
                <TableCell>
                    <FormattedMessage id="cluster.performance" defaultMessage="Performance"/>
                </TableCell>
                <TableCell/>
                <TableCell>
                    <FormattedMessage id="cluster.stowers" defaultMessage="Stowers"/>
                </TableCell>
                <TableCell/>
            </TableRow>
        )
    }

    renderSubHeadings() {
        return (
            <TableRow>
                <TableCell sortColumn="cluster">
                    <FormattedMessage id="cluster.cluster" defaultMessage="Cluster"/>
                </TableCell>
                <TableCell sortColumn="plannedPackages" alignmentHorizontal="right">
                    <FormattedMessage id="cluster.container_planned" defaultMessage="C. Planned"/>
                </TableCell>
                <TableCell sortColumn="receivedPackages" alignmentHorizontal="right">
                    <FormattedMessage id="cluster.arrived" defaultMessage="Arrived"/>
                </TableCell>
                <TableCell sortColumn="currentInductedPackages" alignmentHorizontal="right">
                    <FormattedMessage id="cluster.inducted" defaultMessage="Inducted"/>
                </TableCell>
                {this.isADTAVisibilityEnabled() && getTableCellHeader("currentStowBufferedPackages")}
                <TableCell sortColumn="stowedPackages" alignmentHorizontal="right">
                    <FormattedMessage id="cluster.stowed" defaultMessage="Stowed"/>
                </TableCell>
                <TableCell sortColumn="stowVsPlan" alignmentHorizontal="right">
                    <FormattedMessage id="cluster.stow_plan" defaultMessage="Stow/Plan"/>
                </TableCell>
                <TableCell sortColumn="problemSolvePackages" alignmentHorizontal="right">
                    <FormattedMessage id="cluster.ps" defaultMessage="PS"/>
                </TableCell>
                {this.isADTAVisibilityEnabled() && getTableCellHeader("currentMFRJackpotBufferedPackages")}
                {this.isCartVisibilityEnabled() &&
                    <TableCell sortColumn="missingCarts" alignmentHorizontal="right">
                        <FormattedMessage id="cluster.cartsMissing" defaultMessage="Unopened Carts"/>
                    </TableCell>
                }
                <TableCell sortColumn="binsMissingBag" alignmentHorizontal="right">
                    <FormattedMessage id="cluster.missing" defaultMessage="Missing"/>
                </TableCell>
                <TableCell sortColumn="stowRate" alignmentHorizontal="right">
                    <FormattedMessage id="cluster.stow_rate" defaultMessage="Stow rate"/>
                </TableCell>
                <TableCell sortColumn="scanCompliance" alignmentHorizontal="right">
                    <FormattedMessage id="cluster.scan_complaince" defaultMessage="Scan comp."/>
                </TableCell>
                <TableCell sortColumn="activeStowers" alignmentHorizontal="right">
                    <FormattedMessage id="cluster.active" defaultMessage="Active"/>
                </TableCell>
                <TableCell sortColumn="inactiveStowers" alignmentHorizontal="right">
                    <FormattedMessage id="cluster.inactive" defaultMessage="Inactive"/>
                </TableCell>
            </TableRow>
        )
    }

    renderClusterRow(cluster) {
        const combinedMetricsMap = this.getCombinedMetricsMap(cluster)
        const stowBufferedCell =
            this.getADTATableCell(combinedMetricsMap.currentStowBufferedPackages, cluster, PackageStatus.CURRENT_STOW_BUFFERED)
        const mfrJackpotBufferedCell =
            this.getADTATableCell(combinedMetricsMap.currentMFRJackpotBufferedPackages, cluster, PackageStatus.CURRENT_MFR_JACKPOT_BUFFERED)

        return (
            <TableRow highlightOnHover={true} key={cluster} onClick={() => this.clusterRowClicked(cluster)} data-cy={"clusterDataRow"}>
                <TableCell>{cluster}</TableCell>
                <TableCell alignmentHorizontal="right">
                    {combinedMetricsMap.plannedPackages}
                </TableCell>
                <TableCell alignmentHorizontal="right">
                    {this.renderPackageCount(combinedMetricsMap.receivedPackages, cluster, PackageStatus.CURRENT_RECEIVED)}
                </TableCell>
                <TableCell alignmentHorizontal="right">
                    {this.renderPackageCount(combinedMetricsMap.currentInductedPackages, cluster, PackageStatus.CURRENT_INDUCTED)}
                </TableCell>
                {stowBufferedCell}
                <TableCell alignmentHorizontal="right">
                    {this.renderPackageCount(combinedMetricsMap.stowedPackages, cluster, PackageStatus.STOWED)}
                </TableCell>
                <TableCell alignmentHorizontal="right">
                    {combinedMetricsMap.stowVsPlan}%
                </TableCell>
                <TableCell alignmentHorizontal="right">
                    {this.renderPackageCount(combinedMetricsMap.problemSolvePackages, cluster, PackageStatus.PROBLEM_SOLVE)}
                </TableCell>
                {mfrJackpotBufferedCell}
                {this.isCartVisibilityEnabled()
                        &&
                    <TableCell alignmentHorizontal="right">
                        {combinedMetricsMap.unopenedCarts}
                    </TableCell>
                }
                <TableCell alignmentHorizontal="right">
                    {combinedMetricsMap.binsMissingBag}
                </TableCell>
                <TableCell alignmentHorizontal="right">
                    {combinedMetricsMap.stowRate}
                </TableCell>
                <TableCell alignmentHorizontal="right">
                    {combinedMetricsMap.scanCompliance}%
                </TableCell>
                <TableCell alignmentHorizontal="right">
                    {combinedMetricsMap.activeStowers}
                </TableCell>
                <TableCell alignmentHorizontal="right">
                    {combinedMetricsMap.inactiveStowers}
                </TableCell>
            </TableRow>
        )
    }

    renderTotalClusterRow() {
        const {
            totalStowPackageMetrics, totalInductPackageMetrics, totalStowAssociateMetrics, totalStowBinMetrics,
            totalStowCartMetricsMap
        } = this.props;
        return (
            <TableRow data-cy={"clusterTotalRow"}>
                <TableCell>Total</TableCell>
                <TableCell alignmentHorizontal="right">
                    {getOrDefaultValueForCell(totalStowPackageMetrics, "Total", "plannedPackages")}
                </TableCell>
                <TableCell alignmentHorizontal="right">
                    {this.renderTotalPackageCount(getOrDefaultValueForCell(totalInductPackageMetrics, "Total", "receivedPackages"),
                        PackageStatus.CURRENT_RECEIVED)}
                </TableCell>
                <TableCell alignmentHorizontal="right">
                    {this.renderTotalPackageCount(getOrDefaultValueForCell(totalStowPackageMetrics, "Total", "currentInductedPackages"),
                        PackageStatus.CURRENT_INDUCTED)}
                </TableCell>
                {this.renderStowBufferedTotalCount()}
                <TableCell alignmentHorizontal="right">
                    {getOrDefaultValueForCell(totalStowPackageMetrics, "Total", "stowedPackages")}
                </TableCell>
                <TableCell alignmentHorizontal="right">
                    {getOrDefaultValueForCell(totalStowPackageMetrics, "Total", "stowVsPlan")}%
                </TableCell>
                <TableCell alignmentHorizontal="right">
                    {this.renderTotalPackageCount(getOrDefaultValueForCell(totalStowPackageMetrics, "Total", "problemSolvePackages"),
                        PackageStatus.PROBLEM_SOLVE)}
                </TableCell>
                {this.renderMFRJackpotBufferedTotalCount()}
                <TableCell alignmentHorizontal="right">
                    {getOrDefaultValueForCell(totalStowCartMetricsMap, "Total", "missingCarts")}
                </TableCell>
                <TableCell alignmentHorizontal="right">
                    {getOrDefaultValueForCell(totalStowBinMetrics, "Total", "binsMissingBag")}
                </TableCell>
                <TableCell alignmentHorizontal="right">
                    {getOrDefaultValueForCell(totalStowPackageMetrics, "Total", "stowRate")}
                </TableCell>
                <TableCell alignmentHorizontal="right">
                    {getOrDefaultValueForCell(totalStowPackageMetrics, "Total", "scanCompliance")}%
                </TableCell>
                <TableCell alignmentHorizontal="right">
                    {getOrDefaultValueForCell(totalStowAssociateMetrics, "Total", "activeStowers")}
                </TableCell>
                <TableCell alignmentHorizontal="right">
                    {getOrDefaultValueForCell(totalStowAssociateMetrics, "Total", "inactiveStowers")}
                </TableCell>
            </TableRow>
        )
    }

    renderStowBufferedTotalCount() {
        if (this.isADTAVisibilityEnabled()) {
            return <TableCell alignmentHorizontal="right">
                {this.renderTotalPackageCount(
                    getOrDefaultValueForCell(this.props.totalStowPackageMetrics, "Total", "currentStowBufferedPackages"),
                    PackageStatus.CURRENT_STOW_BUFFERED)}
            </TableCell>
        }
    }

    renderMFRJackpotBufferedTotalCount() {
        if (this.isADTAVisibilityEnabled()) {
            return <TableCell alignmentHorizontal="right">
                {this.renderTotalPackageCount(
                    getOrDefaultValueForCell(this.props.totalStowPackageMetrics, "Total", "currentMFRJackpotBufferedPackages"),
                    PackageStatus.CURRENT_MFR_JACKPOT_BUFFERED)}
            </TableCell>
        }
    }

    getCombinedMetricsMap = cluster => {
        const {
            stowPackageMetricsMap, inductPackageMetricsMap, stowAssociateMetricsMap, stowBinMetricsMap, stowCartMetricsMap
        } = this.props;
        const defaultMetricValue = "-"
        return {
            cluster: cluster,
            binsMissingBag: getOrDefaultValueForCell(stowBinMetricsMap, cluster, "binsMissingBag"),
            unopenedCarts: getOrDefaultValueForCell(stowCartMetricsMap, cluster, "missingCarts"),
            plannedPackages: chainWalk(() => stowPackageMetricsMap[cluster].plannedPackages, defaultMetricValue),
            receivedPackages: chainWalk(() => inductPackageMetricsMap[cluster].receivedPackages, defaultMetricValue),
            currentInductedPackages: chainWalk(() => stowPackageMetricsMap[cluster].currentInductedPackages, defaultMetricValue),
            stowedPackages: chainWalk(() => stowPackageMetricsMap[cluster].stowedPackages, defaultMetricValue),
            stowVsPlan: chainWalk(() => stowPackageMetricsMap[cluster].stowVsPlan, defaultMetricValue),
            problemSolvePackages: chainWalk(() => stowPackageMetricsMap[cluster].problemSolvePackages, defaultMetricValue),
            stowRate: chainWalk(() => stowPackageMetricsMap[cluster].stowRate, defaultMetricValue),
            scanCompliance: chainWalk(() => stowPackageMetricsMap[cluster].scanCompliance, defaultMetricValue),
            currentStowBufferedPackages: chainWalk(() => stowPackageMetricsMap[cluster].currentStowBufferedPackages, defaultMetricValue),
            currentMFRJackpotBufferedPackages: chainWalk(() => stowPackageMetricsMap[cluster].currentMFRJackpotBufferedPackages, defaultMetricValue),
            activeStowers: chainWalk(() => stowAssociateMetricsMap[cluster].activeStowers, defaultMetricValue),
            inactiveStowers: chainWalk(() => stowAssociateMetricsMap[cluster].inactiveStowers, defaultMetricValue)
        }
    }

    clusterRowClicked = clickedCluster => {
        this.props.dispatch(navigationAdded({
            key: 'Cluster',
            value: clickedCluster
        }))
    }

    renderTotalPackageCount = (value, state) => {
        const params = {
            PACKAGE_STATUS: state
        }
        return value !== 0 && value !== '-' ?
            <Link href={this.getPackageListURL(params)}
                  type="secondary"> {value} </Link> : value
    }

    getADTATableCell = (value, aisle, state) => {
        return this.isADTAVisibilityEnabled() && getTableCellCount(this.renderPackageCount(value, aisle, state))
    }

    renderPackageCount = (value, cluster, state) => {
        const params = {
            PACKAGE_STATUS: state,
            CLUSTER: cluster
        }
        return value !== 0 && value !== '-' ?
            <Link href={this.getPackageListURL(params)}
                  type="secondary"> {value} </Link> : value
    }

    getPackageListURL = (params) => {
        const options = this.props.options
        if (options.cycle) {
            params["CYCLE"] = options.cycle;
        }
        return "#packageList/?" + formQueryParams(params);
    }

    isADTAVisibilityEnabled = () => {
        return this.props.featureStatus && this.props.featureStatus[FEATURES.ADTA_VISIBILITY];
    }

    isDRSStowVisibilityEnabled = () => {
        return this.props.featureStatus && this.props.featureStatus[FEATURES.DRS_STOW_VISIBILITY];
    }

    isCartVisibilityEnabled = () => {
        return this.props.featureStatus && this.props.featureStatus[FEATURES.CART_VISIBILITY];
    }
}

const mapStateToProps = state => ({
    loading: state.clusterReducer.loading,
    error: state.clusterReducer.error,
    options: state.optionsReducer.options,
    stowPackageMetricsMap: state.clusterReducer.stowPackageMetricsMap,
    stowBinMetricsMap: state.clusterReducer.stowBinMetricsMap,
    stowCartMetricsMap : state.clusterReducer.stowCartMetricsMap,
    inductPackageMetricsMap: state.clusterReducer.inductPackageMetricsMap,
    stowAssociateMetricsMap: state.clusterReducer.stowAssociateMetricsMap,
    totalStowPackageMetrics: state.clusterReducer.totalStowPackageMetrics,
    totalStowBinMetrics: state.clusterReducer.totalStowBinMetrics,
    totalStowCartMetricsMap: state.clusterReducer.totalStowCartMetricsMap,
    totalInductPackageMetrics: state.clusterReducer.totalInductPackageMetrics,
    totalStowAssociateMetrics: state.clusterReducer.totalStowAssociateMetrics,
    featureStatus: state.configReducer.featureStatus
});
export default withRouter(connect(mapStateToProps)(Cluster));



// WEBPACK FOOTER //
// ./src/components/Cluster.js